import { getChainConfig } from "@silo/config";
import { Address, ChainKey, chains, getIsWrappedNative } from "@silo/types";
import { Effect, pipe, Option } from "effect";

interface TokenLabels {
  name: string;
  symbol: string;
}

export type GetTokenLabels = ReturnType<typeof getTokenLabels>;
export type GetTokenLabelsSuccess = Effect.Effect.Success<GetTokenLabels>;
export type GetTokenLabelsError = Effect.Effect.Error<GetTokenLabels>;
export type GetTokenLabelsContext = Effect.Effect.Context<GetTokenLabels>;

interface GetTokenLabelsParams {
  chainKey: ChainKey;
  tokenAddress: Address;
  rawSymbol: string;
  rawName: string;
}

export function getTokenLabels({
  tokenAddress,
  chainKey,
  rawSymbol,
  rawName,
}: GetTokenLabelsParams) {
  return Effect.gen(function* () {
    const chainConfig = getChainConfig(chainKey);

    const tokenConfig = pipe(
      chainConfig.tokens[tokenAddress],
      Option.fromNullable
    );

    const isWrappedNative = getIsWrappedNative(chainKey)(tokenAddress);
    const { nativeCurrency } = chains[chainKey];

    /**
     * For symbol and name:
     *
     * - if there is a config value, use that
     * - if the token is the wrapped native, use the native value
     * - fall back to raw symbol/name otherwise
     */

    const symbol = pipe(
      tokenConfig,
      Option.flatMapNullable((config) => config.symbol),
      Option.orElse(() =>
        isWrappedNative ? Option.some(nativeCurrency.symbol) : Option.none()
      ),
      Option.getOrElse(() => rawSymbol)
    );

    const name = pipe(
      tokenConfig,
      Option.flatMapNullable((config) => config.name),
      Option.orElse(() =>
        isWrappedNative ? Option.some(nativeCurrency.name) : Option.none()
      ),
      Option.getOrElse(() => rawName)
    );

    const tokenLabels: TokenLabels = { symbol, name };

    return tokenLabels;
  });
}
